"use client";

import { useCallback, useEffect, useRef } from "react";
import Image from "next/image";
import Link from "next/link";
import { AnimatePresence, motion } from "framer-motion";
import { FormattedMessage, useIntl } from "react-intl";
import {
  FaArrowRight,
  FaCircleCheck,
  FaGift,
  FaXmark,
} from "react-icons/fa6";

import capImage from "@/app/assets/cap/CAP_PNG.png";
import { useExitIntent } from "@/app/hooks/useExitIntent";
import { useFocusTrap } from "@/app/hooks/useFocusTrap";
import {
  trackCtaClick,
  trackEvent,
} from "@/app/providers/ObservabilityProvider";
import { useAppLink } from "@/lib/analytics/app-links";

const PERK_KEYS = ["exit.perk1", "exit.perk2", "exit.perk3"];

type DismissReason = "close-button" | "stay-button" | "overlay" | "escape";

/** Displays and tracks the single-session exit-intent retention offer. */
export default function ExitIntentModal() {
  const intl = useIntl();
  const { href: appUrl, target: destination } = useAppLink("exit-intent");
  const { isOpen, trigger, close } = useExitIntent();
  const shownTrackedRef = useRef(false);
  const terminalOutcomeRef = useRef<"cta" | "dismissed" | null>(null);
  const pendingDismissRef = useRef(false);
  const cardRef = useRef<HTMLDivElement>(null);
  // Modal: Tab stays inside the card, focus goes back where it was on close.
  useFocusTrap(cardRef, isOpen);

  const dismiss = useCallback(
    (dismissReason: DismissReason) => {
      if (terminalOutcomeRef.current !== null || pendingDismissRef.current) {
        close();
        return;
      }

      // Defer the dismissal event by one microtask so a CTA produced by the
      // same interaction wins without emitting two terminal outcomes.
      pendingDismissRef.current = true;
      close();
      queueMicrotask(() => {
        pendingDismissRef.current = false;
        if (terminalOutcomeRef.current !== null) return;

        terminalOutcomeRef.current = "dismissed";
        void trackEvent("exit-intent-dismissed", {
          schemaVersion: "1",
          trigger: trigger ?? "unknown",
          destination,
          dismissReason,
        });
      });
    },
    [close, destination, trigger],
  );

  const handleCta = useCallback(() => {
    if (terminalOutcomeRef.current !== null) return;

    terminalOutcomeRef.current = "cta";
    pendingDismissRef.current = false;
    void trackEvent(
      "exit-intent-cta",
      {
        schemaVersion: "1",
        trigger: trigger ?? "unknown",
        destination,
      },
      { criticalOnNav: true },
    );
    trackCtaClick("exit-intent", destination);
  }, [destination, trigger]);

  useEffect(() => {
    if (!isOpen) {
      shownTrackedRef.current = false;
      terminalOutcomeRef.current = null;
      pendingDismissRef.current = false;
      return;
    }

    if (!shownTrackedRef.current) {
      shownTrackedRef.current = true;
      void trackEvent("exit-intent-shown", {
        schemaVersion: "1",
        trigger: trigger ?? "unknown",
        destination,
      });
    }

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") dismiss("escape");
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [destination, dismiss, isOpen, trigger]);

  return (
    <AnimatePresence>
      {isOpen && (
        <div
          className="fixed inset-0 z-50 overflow-y-auto"
          onClick={() => dismiss("overlay")}
        >
          {/* Backdrop — fixed so it keeps covering the viewport while the
              overlay above scrolls */}
          <motion.div
            className="fixed inset-0 bg-black/60"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          />

          {/* Centers the card on tall viewports and scrolls instead of clipping
              it on short ones (mobile landscape). The vertical padding also
              leaves room for Cap, who overflows above the card. */}
          <div className="flex min-h-full items-center justify-center px-4 py-20">
            {/* Modal card */}
            <motion.div
              ref={cardRef}
              role="dialog"
              aria-modal="true"
              aria-labelledby="exit-intent-title"
              className="relative z-10 w-full max-w-sm rounded-3xl bg-surface shadow-2xl"
              initial={{ opacity: 0, scale: 0.94, y: 16 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.94, y: 16 }}
              transition={{ duration: 0.25, ease: "easeOut" }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Cap peeking over the top edge */}
              <motion.div
                className="pointer-events-none absolute -top-19 left-1/2 w-26 -translate-x-1/2 select-none"
                initial={{ y: 24, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{
                  delay: 0.12,
                  type: "spring",
                  stiffness: 200,
                  damping: 18,
                }}
              >
                <Image
                  src={capImage}
                  alt={intl.formatMessage({ id: "a11y.cap" })}
                  className="h-auto w-full"
                />
              </motion.div>

              {/* Close */}
              <div className="flex justify-end px-5 pt-5">
                <button
                  onClick={() => dismiss("close-button")}
                  aria-label={intl.formatMessage({ id: "a11y.close" })}
                  autoFocus
                  className="flex h-7 w-7 items-center justify-center rounded-full bg-gray-ulight text-gray-main transition-colors hover:bg-gray-light"
                >
                  <FaXmark aria-hidden="true" size={13} />
                </button>
              </div>

              <div className="flex flex-col items-center px-6 pb-6">
                {/* Offer pill */}
                <div className="flex items-center gap-2 rounded-full bg-blue-ulight px-4 py-1.5 text-sm font-semibold text-brand-ink">
                  <FaGift aria-hidden="true" className="shrink-0" />
                  <FormattedMessage id="hero.freeBadge" />
                </div>

                <h2
                  id="exit-intent-title"
                  className="mt-3 text-center text-2xl leading-tight font-bold text-brand"
                >
                  <FormattedMessage id="exit.title" />
                </h2>

                <p className="mt-2 text-center text-sm leading-relaxed text-gray-main">
                  <FormattedMessage id="exit.description" />
                </p>

                {/* Perks */}
                <ul className="mt-5 w-full space-y-2.5">
                  {PERK_KEYS.map((key) => (
                    <li
                      key={key}
                      className="flex items-start gap-2.5 text-sm text-text-main"
                    >
                      <FaCircleCheck
                        aria-hidden="true"
                        className="mt-0.5 shrink-0 text-green-main"
                      />
                      <FormattedMessage id={key} />
                    </li>
                  ))}
                </ul>

                {/* Primary CTA */}
                <Link
                  href={appUrl}
                  onClick={handleCta}
                  className="mt-6 flex w-full items-center justify-center gap-2 rounded-2xl bg-blue-main py-3 text-sm font-semibold text-white transition-colors hover:bg-[#4F46E5]"
                >
                  <FormattedMessage id="exit.cta" />
                  <FaArrowRight aria-hidden="true" />
                </Link>

                {/* Secondary — stay on the page */}
                <button
                  onClick={() => dismiss("stay-button")}
                  className="mt-2 w-full rounded-2xl py-2.5 text-sm font-semibold text-gray-main transition-colors hover:bg-gray-ulight hover:text-text-main"
                >
                  <FormattedMessage id="exit.stay" />
                </button>

                <p className="mt-3 text-center text-xs text-gray-main">
                  <FormattedMessage id="hero.users" />
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      )}
    </AnimatePresence>
  );
}
